import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import type { ChatResponseDto } from '../models/orpheus.dto'
import type { WorkspaceId } from '../models/orpheus.types'
import type { OrpheusKeyError } from '../models/orpheusKey.types'
import type { StreamState } from '../models/stream.types'
import { mapResponse } from '../service/orpheusChat'
import { sortThreads } from '../service/threadMapper'

import {
  createOrpheusThreadState,
  type HistoryLoadResult,
  type ThreadReadApi,
} from './orpheusThreadState'
import { createEmptyMessage, type LogicalTurn, type PrepareTurnInput } from './orpheusTurnState'

export type { LogicalTurn, PrepareTurnInput } from './orpheusTurnState'

/**
 * Module 18's non-persisted conversation owner. Transport and credit orchestration stay in the
 * flow; this store owns optimistic identity, turn transitions, workspace/thread ownership, and
 * the AbortController for the one active turn.
 */
export const useOrpheusStore = defineStore('orpheusStore', () => {
  const threadState = createOrpheusThreadState()
  const { threads, selectedLocalId, findOwnedThread } = threadState
  const sending = ref(false)
  const streamState = ref<StreamState>('idle')
  const streamAbort = shallowRef<AbortController | null>(null)
  const activeTurnId = ref<string | null>(null)
  const activeWorkspaceId = ref<WorkspaceId | null>(null)
  const activeThreadLocalId = ref<string | null>(null)
  const turns = ref<Record<string, LogicalTurn>>({})

  const activeAssistantMessage = computed(() => {
    const turnId = activeTurnId.value
    const threadLocalId = activeThreadLocalId.value
    if (!turnId || !threadLocalId) return null
    const thread = findOwnedThread(activeWorkspaceId.value, threadLocalId)
    return (
      thread?.messages.find(
        (message) => message.turnId === turnId && message.role === 'assistant',
      ) ?? null
    )
  })
  const isStreaming = computed(
    () => streamState.value === 'connecting' || streamState.value === 'streaming',
  )

  function prepareTurn(input: PrepareTurnInput): LogicalTurn | null {
    if (activeTurnId.value) cancelStream()
    threadState.claimThreadWorkspace(input.workspaceId)
    if (input.replaceAssistantLocalId) return prepareReplay(input, input.replaceAssistantLocalId)

    let thread = findOwnedThread(input.workspaceId, input.threadLocalId)
    if (!thread) {
      thread = {
        workspaceId: input.workspaceId,
        threadId: null,
        localId: input.threadLocalId,
        conversationId: null,
        title: input.text,
        bookmarked: false,
        createdAt: input.now,
        updatedAt: input.now,
        lastMessagePreview: input.text,
        messageCount: 0,
        messages: [],
        historyLoaded: true,
        historyLoading: false,
      }
      threads.value = [thread, ...threads.value]
    }

    const editIndex = input.editIndex
    const baseMessages =
      editIndex === undefined
        ? thread.messages
        : thread.messages.slice(0, Math.max(0, Math.min(editIndex, thread.messages.length)))
    const userIndex = baseMessages.length
    const user = createEmptyMessage({
      localId: input.userLocalId,
      turnId: input.turnId,
      role: 'user',
      content: input.text,
      index: userIndex,
      now: input.now,
      streamState: 'completed',
    })
    if (input.attachment) user.attachment = input.attachment
    const assistant = createEmptyMessage({
      localId: input.assistantLocalId,
      turnId: input.turnId,
      role: 'assistant',
      content: '',
      index: userIndex + 1,
      now: input.now,
      streamState: 'connecting',
    })

    thread.messages = [...baseMessages, user, assistant]
    thread.updatedAt = input.now
    thread.lastMessagePreview = input.text
    thread.messageCount = thread.messages.length
    thread.historyLoaded = true
    if (editIndex === 0) thread.title = input.text

    const turn: LogicalTurn = {
      turnId: input.turnId,
      idempotencyKey: input.idempotencyKey,
      workspaceId: input.workspaceId,
      threadLocalId: thread.localId,
      requestThreadId: thread.threadId,
      text: input.text,
      recommendationDecision: input.recommendationDecision ?? null,
      userLocalId: input.userLocalId,
      assistantLocalId: input.assistantLocalId,
      state: 'connecting',
      attempts: 1,
      creditsApplied: false,
      requestExtras: input.requestExtras ?? {},
    }
    turns.value[input.turnId] = turn
    selectedLocalId.value = thread.localId
    beginOwnership(turn)
    threads.value = sortThreads(threads.value)
    return turn
  }

  /**
   * The same question once more, into the answer slot it failed in: no second user message, and
   * the card turns back into the waiting state where it stood (v3 `replaceIndex`).
   */
  function prepareReplay(input: PrepareTurnInput, assistantLocalId: string): LogicalTurn | null {
    const thread = findOwnedThread(input.workspaceId, input.threadLocalId)
    const index = thread?.messages.findIndex(
      (message) => message.localId === assistantLocalId && message.role === 'assistant',
    )
    if (!thread || index === undefined || index < 0) return null

    const assistant = createEmptyMessage({
      localId: assistantLocalId,
      turnId: input.turnId,
      role: 'assistant',
      content: '',
      index,
      now: input.now,
      streamState: 'connecting',
    })
    thread.messages.splice(index, 1, assistant)
    thread.updatedAt = input.now

    const turn: LogicalTurn = {
      turnId: input.turnId,
      idempotencyKey: input.idempotencyKey,
      workspaceId: input.workspaceId,
      threadLocalId: thread.localId,
      requestThreadId: thread.threadId,
      text: input.text,
      recommendationDecision: null,
      userLocalId: thread.messages[index - 1]?.localId ?? input.userLocalId,
      assistantLocalId,
      state: 'connecting',
      attempts: 1,
      creditsApplied: false,
      requestExtras: input.requestExtras ?? {},
    }
    turns.value[input.turnId] = turn
    selectedLocalId.value = thread.localId
    beginOwnership(turn)
    return turn
  }

  function prepareResume(turnId: string): LogicalTurn | null {
    const turn = turns.value[turnId]
    if (!turn || turn.state !== 'reconnectable-error') return null
    const thread = findOwnedThread(turn.workspaceId, turn.threadLocalId)
    const assistant = thread?.messages.find(
      (message) => message.localId === turn.assistantLocalId && message.turnId === turn.turnId,
    )
    if (!thread || !assistant) return null

    if (activeTurnId.value) cancelStream()
    // No resume-offset contract exists yet. Reuse the same slot and replay into an empty buffer so
    // a future transport retry cannot duplicate already-rendered deltas.
    assistant.content = ''
    assistant.kind = 'text'
    delete assistant.errorKey
    assistant.streamState = 'connecting'
    assistant.answer = null
    assistant.recommendations = []
    assistant.recommendationData = null
    assistant.responseBlocks = []
    assistant.followups = []
    assistant.suggestedPrompts = []
    assistant.channels = []
    assistant.controllerParams = null
    delete assistant.setupStep
    delete assistant.setupCard
    delete assistant.filePreview
    delete assistant.clarification
    delete assistant.credits
    delete assistant.keyError
    turn.state = 'connecting'
    turn.attempts += 1
    beginOwnership(turn)
    return turn
  }

  function beginOwnership(turn: LogicalTurn): void {
    streamAbort.value = new AbortController()
    activeTurnId.value = turn.turnId
    activeWorkspaceId.value = turn.workspaceId
    activeThreadLocalId.value = turn.threadLocalId
    sending.value = true
    streamState.value = 'connecting'
  }

  function ownsTurn(turnId: string, workspaceId: WorkspaceId): boolean {
    const turn = turns.value[turnId]
    return Boolean(
      turn &&
      activeTurnId.value === turnId &&
      String(activeWorkspaceId.value) === String(workspaceId) &&
      String(turn.workspaceId) === String(workspaceId) &&
      findOwnedThread(workspaceId, turn.threadLocalId),
    )
  }

  function markStreaming(turnId: string, workspaceId: WorkspaceId): boolean {
    if (!ownsTurn(turnId, workspaceId)) return false
    const turn = turns.value[turnId]
    const assistant = activeAssistantMessage.value
    if (!turn || !assistant) return false
    turn.state = 'streaming'
    assistant.streamState = 'streaming'
    streamState.value = 'streaming'
    return true
  }

  function appendDelta(turnId: string, workspaceId: WorkspaceId, delta: string): boolean {
    if (!delta || !markStreaming(turnId, workspaceId)) return false
    const assistant = activeAssistantMessage.value
    if (!assistant) return false
    assistant.content += delta
    return true
  }

  function applyFinal(
    turnId: string,
    workspaceId: WorkspaceId,
    response: ChatResponseDto,
  ): boolean {
    if (!ownsTurn(turnId, workspaceId)) return false
    const turn = turns.value[turnId]
    const thread = turn ? findOwnedThread(workspaceId, turn.threadLocalId) : undefined
    if (!turn || !thread) return false
    const assistantIndex = thread.messages.findIndex(
      (message) => message.localId === turn.assistantLocalId && message.turnId === turnId,
    )
    if (assistantIndex < 0) return false

    const mapped = mapResponse(response, {
      localId: turn.assistantLocalId,
      turnId,
      index: assistantIndex,
    })
    thread.messages.splice(assistantIndex, 1, mapped)
    thread.threadId = response.thread_id || thread.threadId
    thread.conversationId = response.conversation_id ?? thread.conversationId
    thread.title = response.title ?? thread.title
    thread.createdAt = response.created_at ?? thread.createdAt
    thread.updatedAt = response.updated_at ?? response.created_at ?? thread.updatedAt
    thread.lastMessagePreview = mapped.content
    thread.messageCount = thread.messages.length
    thread.historyLoaded = true
    turn.state = 'completed'
    mapped.streamState = 'completed'
    streamState.value = 'completed'
    sending.value = false
    clearActiveOwnership()
    threads.value = sortThreads(threads.value)
    return true
  }

  function failTurn(
    turnId: string,
    workspaceId: WorkspaceId,
    state: 'reconnectable-error' | 'terminal-error',
    errorKey: string,
    keyError?: OrpheusKeyError,
  ): boolean {
    if (!ownsTurn(turnId, workspaceId)) return false
    const turn = turns.value[turnId]
    const assistant = activeAssistantMessage.value
    if (!turn || !assistant) return false
    assistant.kind = 'error'
    assistant.errorKey = errorKey
    if (keyError) assistant.keyError = keyError
    assistant.streamState = state
    turn.state = state
    streamState.value = state
    sending.value = false
    clearActiveOwnership()
    return true
  }

  function markCreditsApplied(turnId: string): boolean {
    const turn = turns.value[turnId]
    if (!turn || turn.creditsApplied || turn.state !== 'completed') return false
    turn.creditsApplied = true
    return true
  }

  function cancelStream(): void {
    const controller = streamAbort.value
    const turnId = activeTurnId.value
    const workspaceId = activeWorkspaceId.value
    controller?.abort()
    if (turnId && workspaceId !== null && ownsTurn(turnId, workspaceId)) {
      const turn = turns.value[turnId]
      const assistant = activeAssistantMessage.value
      if (turn) turn.state = 'canceled'
      if (assistant) assistant.streamState = 'canceled'
      streamState.value = 'canceled'
    }
    sending.value = false
    clearActiveOwnership()
  }

  function setSelectedThread(localId: string | null): void {
    if (activeThreadLocalId.value && localId !== activeThreadLocalId.value) cancelStream()
    selectedLocalId.value = localId
  }

  async function selectThread(
    workspaceId: WorkspaceId,
    localId: string | null,
    api?: ThreadReadApi,
  ): Promise<HistoryLoadResult> {
    if (threadState.selectionMode.value) return 'missing'
    if (activeThreadLocalId.value && localId !== activeThreadLocalId.value) cancelStream()
    return api
      ? threadState.selectThreadWithHistory(workspaceId, localId, api)
      : threadState.selectThreadWithHistory(workspaceId, localId)
  }

  function startNewChat(): void {
    cancelStream()
    selectedLocalId.value = null
    streamState.value = 'idle'
  }

  function clearActiveOwnership(): void {
    streamAbort.value = null
    activeTurnId.value = null
    activeWorkspaceId.value = null
    activeThreadLocalId.value = null
  }

  function reset(): void {
    streamAbort.value?.abort()
    threadState.resetThreadState()
    turns.value = {}
    sending.value = false
    streamState.value = 'idle'
    clearActiveOwnership()
  }

  return {
    ...threadState,
    sending,
    streamState,
    streamAbort,
    activeTurnId,
    activeWorkspaceId,
    activeThreadLocalId,
    activeAssistantMessage,
    isStreaming,
    turns,
    prepareTurn,
    prepareResume,
    ownsTurn,
    markStreaming,
    appendDelta,
    applyFinal,
    failTurn,
    markCreditsApplied,
    cancelStream,
    setSelectedThread,
    selectThread,
    startNewChat,
    reset,
  }
})

export type OrpheusStore = ReturnType<typeof useOrpheusStore>
